import {
  buildUseQuery,
  FlexRowCenterHorizontal,
  LoadingSpinner,
  logger,
} from '@main/core-ui';
import { endpoints, PrivacyCenterTranslation } from '@main/pc-types';
import NotFound from '@main/privacy-center/src/containers/NotFound';
import { moment } from '@main/utils';
import { LanguageKey } from '@transcend-io/internationalization';
import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useStaticPrivacyCenterModule } from '../../hooks';
import { PRIVACY_CENTER_TRANSLATIONS } from '../../translations';
import { selectCachedLocale, selectTranslations } from './selectors';
import { setTranslations } from './slice';

/**
 * Props for LocalizedRouter
 */
export interface LocalizedRouterProps {
  /** Children */
  children: JSX.Element;
}

const usePrivacyCenterTranslatedMessages = buildUseQuery(
  endpoints.privacyCenterTranslatedMessages,
);

/**
 * Provider that filters for organization supported locales
 */
export function LangProvider({
  children,
  lang,
}: LocalizedRouterProps & {
  /** The raw language being requested */
  lang: string;
}): JSX.Element {
  const dispatch = useDispatch();

  // cached locales
  const cachedLocale = useSelector(selectCachedLocale);
  const translations = useSelector(selectTranslations);

  // Fetch translations
  const { directData, directError, skipGql } = useStaticPrivacyCenterModule<
    PrivacyCenterTranslation[]
  >('privacyCenterMessages', lang);
  const { data: gqlData, error: gqlError } = usePrivacyCenterTranslatedMessages(
    {
      variables: {
        input: { locale: lang },
      },
      skip: skipGql,
    },
  );
  const data = skipGql ? directData : gqlData;
  const error = skipGql ? directError : gqlError;

  // Log error
  useEffect(() => {
    if (error) {
      // Log out an error here for monitoring purposes, but gracefully fallback to default messages
      logger.error(error);
    }
  }, [error]);

  // Whenever the network request ends, we set translations to be rendered.
  // Even if the network request fails, we want to fallback to our default
  // translations and send the error to monitoring.
  useEffect(() => {
    if (data && lang) {
      moment.locale(lang);
      PRIVACY_CENTER_TRANSLATIONS[lang]().then((loaded) => {
        dispatch(
          setTranslations({
            locale: lang as LanguageKey,
            translations: {
              ...loaded.default,
              ...data.reduce(
                (acc, { id, value }) => Object.assign(acc, { [id]: value }),
                {},
              ),
            },
          }),
        );
      });
    }
  }, [data, dispatch, lang]);

  return (
    <IntlProvider locale={lang as LanguageKey} messages={translations || {}}>
      {/**
       * If the cache is the same locale as the one being requested,
       * serve the cache while we wait for the network request to complete
       */}
      {cachedLocale === lang ? (
        children
      ) : error ? (
        <NotFound />
      ) : (
        <FlexRowCenterHorizontal>
          <LoadingSpinner />
        </FlexRowCenterHorizontal>
      )}
    </IntlProvider>
  );
}
